import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { View, StyleSheet, FlatList, RefreshControl } from 'react-native';
import { useAppSelector, useAppDispatch } from '@/store';
import { selectSortedEvents } from '@/store/selectors/residentsEventsSelectors';
import { fetchHomeData } from '@/store/slices/residents/homeSlice';
import { Event as ResioEvent } from '@/types/residents/event';
import { eventStrings } from '@/constants/residents/strings';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

import { EventsScreenSkeleton } from './EventsScreenSkeleton';
import {
  PageHeader,
  EventGlassCard,
  EventsGlassTabFilter,
  EventGlassEmptyState,
} from '@/components/residents/redesign';
import type { EventTabType } from '@/components/residents/redesign';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';

const HP = REDESIGN_SPACING.horizontalPadding;

export const ResidentEventsScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { isLoading } = useAppSelector((state) => state.home);
  const events = useAppSelector(selectSortedEvents);
  const propertyName = user?.property?.marketingName || user?.property?.legalName || '';

  const [refreshing, setRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState<EventTabType>('upcoming');

  useEffect(() => {
    if (user?.propertyId) {
      dispatch(fetchHomeData(user.propertyId));
    }
  }, [dispatch, user?.propertyId]);

  const handleTabChange = useCallback((tab: EventTabType) => {
    setSelectedTab(tab);
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (user?.propertyId) {
        await dispatch(fetchHomeData(user.propertyId)).unwrap();
      }
    } catch (error) {
      console.error('Failed to refresh events:', error);
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, user?.propertyId]);

  const filteredEvents = useMemo(() => {
    const validEvents = events.filter((event) => event && event.id);
    const now = new Date();

    if (selectedTab === 'upcoming') {
      return validEvents.filter((event) => {
        if (!event.startTime) return true;
        return new Date(event.startTime) >= now;
      });
    }
    return validEvents.filter((event) => {
      if (!event.startTime) return false;
      return new Date(event.startTime) < now;
    });
  }, [events, selectedTab]);

  const renderEventItem = useCallback(({ item }: { item: ResioEvent }) => (
    <EventGlassCard event={item} isPast={selectedTab === 'past'} />
  ), [selectedTab]);

  const keyExtractor = useCallback((item: ResioEvent) => `event-${item.id}`, []);

  const ListHeaderComponent = useMemo(() => (
    <View style={styles.headerSection}>
      <ScreenBackLink />
      <PageHeader title="Events" subtitle={propertyName} />
      <EventsGlassTabFilter selectedTab={selectedTab} onTabChange={handleTabChange} />
    </View>
  ), [propertyName, selectedTab, handleTabChange]);

  const ListEmptyComponent = useMemo(() => {
    if (isLoading && !events.length) {
      return <EventsScreenSkeleton />;
    }
    const isUpcoming = selectedTab === 'upcoming';
    const emptyContent = isUpcoming ? eventStrings.empty : eventStrings.emptyPast;
    return <EventGlassEmptyState title={emptyContent.title} message={emptyContent.description} />;
  }, [isLoading, events.length, selectedTab]);

  return (
    <FlatList
      data={filteredEvents}
      keyExtractor={keyExtractor}
      renderItem={renderEventItem}
      ListHeaderComponent={ListHeaderComponent}
      ListEmptyComponent={ListEmptyComponent}
      contentContainerStyle={styles.listContent}
      ItemSeparatorComponent={ItemSeparator}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={handleRefresh}
          tintColor={colors.text.primary}
        />
      }
    />
  );
};

const ItemSeparator = () => <View style={styles.separator} />;

const styles = StyleSheet.create({
  headerSection: {
    paddingBottom: 8,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  listContent: {
    paddingHorizontal: HP,
    paddingBottom: 40,
    flexGrow: 1,
  },
  separator: {
    height: 16,
  },
});

export default ResidentEventsScreen;
